/**
 * Unified analytics core — ONE call fans an event out to all three providers:
 *   • PostHog        — product analytics + funnels (posthog.capture)
 *   • Datadog RUM    — business events alongside perf/errors (datadogRum.addAction)
 *   • Google Analytics 4 — marketing/attribution (gtag('event', ...))
 *
 * Design goals:
 *   - Context-free: plain module functions, callable from anywhere (public marketing
 *     pages, auth screens, server-action client callbacks, deep in the app) without a
 *     React provider. The old `useAnalytics` hook only reached PostHog and only worked
 *     inside the authenticated app — top-of-funnel events were silently dropped.
 *   - Consent-gated: nothing is sent until the visitor accepts (see ./consent). Events
 *     fired before a decision are BUFFERED and flushed on accept, so the first-touch
 *     funnel steps (landing → pricing → signup) aren't lost.
 *   - Defensive: each provider call is guarded + try/caught, so a missing/late SDK
 *     never throws into product code.
 *
 * Provider SDKs are read lazily (posthog singleton import; datadogRum import; window.gtag)
 * so this module is safe to import from any bundle, including SSR.
 */

import posthog from 'posthog-js';
import { datadogRum } from '@datadog/browser-rum';
import { hasAnalyticsConsent } from './consent';
import { scrubAuthTokensFromUrl } from '@/utils/authUrlScrub';

export type AnalyticsValue = string | number | boolean | string[] | null | undefined;
export type AnalyticsProperties = Record<string, AnalyticsValue>;
export type AnalyticsTraits = Record<string, AnalyticsValue>;

type QueuedEvent = { name: string; properties?: AnalyticsProperties };

// Buffer events fired before consent so we don't lose first-touch funnel steps.
// Capped to avoid unbounded growth if a visitor never decides.
const MAX_BUFFER = 50;
const preConsentBuffer: QueuedEvent[] = [];

declare global {
  interface Window {
    gtag?: (command: string, ...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

function sendToPostHog(name: string, properties?: AnalyticsProperties): void {
  try {
    if (typeof posthog?.capture === 'function' && posthog.__loaded) {
      posthog.capture(name, properties);
    }
  } catch {
    /* never throw into product code */
  }
}

function sendToDatadog(name: string, properties?: AnalyticsProperties): void {
  try {
    // addAction surfaces the event as a RUM custom action, queryable alongside
    // the same session's perf/errors/replay — the missing "business event" layer.
    datadogRum.addAction(name, properties as Record<string, unknown> | undefined);
  } catch {
    /* RUM not initialized yet — drop silently */
  }
}

function sendToGa(name: string, properties?: AnalyticsProperties): void {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', name, properties ?? {});
    }
  } catch {
    /* gtag not loaded on this surface — drop silently */
  }
}

function dispatch(name: string, properties?: AnalyticsProperties): void {
  sendToPostHog(name, properties);
  sendToDatadog(name, properties);
  sendToGa(name, properties);
}

/**
 * Track a business/funnel event. Fans out to all three providers when consent is
 * granted; buffers (up to MAX_BUFFER) until then. No-op on the server.
 */
export function track(name: string, properties?: AnalyticsProperties): void {
  if (typeof window === 'undefined' || !name) return;

  if (!hasAnalyticsConsent()) {
    if (preConsentBuffer.length < MAX_BUFFER) {
      preConsentBuffer.push({ name, properties });
    }
    return;
  }

  dispatch(name, properties);
}

/**
 * Flush any events buffered before consent. Call once, immediately after the
 * visitor accepts (the AnalyticsProvider wires this to the consent subscription).
 */
export function flushPreConsentEvents(): void {
  if (!hasAnalyticsConsent()) return;
  while (preConsentBuffer.length > 0) {
    const e = preConsentBuffer.shift();
    if (e) dispatch(e.name, e.properties);
  }
}

/**
 * Identify the current user across all providers — the stitch that joins the
 * anonymous top-of-funnel session to the signed-up user so the full funnel is
 * one continuous identity. Safe to call repeatedly (providers dedupe).
 */
export function identifyUser(userId: string, traits?: AnalyticsTraits): void {
  if (typeof window === 'undefined' || !userId || !hasAnalyticsConsent()) return;

  try {
    if (posthog?.__loaded) posthog.identify(userId, traits);
  } catch {
    /* noop */
  }
  try {
    datadogRum.setUser({ id: userId, ...(traits as Record<string, unknown>) });
  } catch {
    /* noop */
  }
  try {
    // GA: associate hits with a stable user id for cross-device/session funnels.
    if (typeof window.gtag === 'function') {
      window.gtag('set', { user_id: userId });
    }
  } catch {
    /* noop */
  }
}

/** Clear identity on logout so a shared device doesn't bleed sessions together. */
export function resetUser(): void {
  if (typeof window === 'undefined') return;
  try {
    if (posthog?.__loaded) posthog.reset();
  } catch {
    /* noop */
  }
  try {
    datadogRum.clearUser();
  } catch {
    /* noop */
  }
  try {
    // GA4 has no first-class unset; null is the documented idempotent reset for the
    // config-level user_id. Without this, a shared browser keeps attributing the next
    // visitor's hits to the prior user until the tab closes.
    if (typeof window.gtag === 'function') window.gtag('set', { user_id: null });
  } catch {
    /* noop */
  }
}

/**
 * Explicit pageview — used by the SPA route tracker. PostHog/GA both want the
 * full URL; Datadog tracks views automatically so we only feed PostHog + GA here.
 */
export function trackPage(url?: string): void {
  if (typeof window === 'undefined' || !hasAnalyticsConsent()) return;
  const current = scrubAuthTokensFromUrl(url || window.location.href);
  try {
    if (posthog?.__loaded) posthog.capture('$pageview', { $current_url: current });
  } catch {
    /* noop */
  }
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', { page_location: current });
    }
  } catch {
    /* noop */
  }
}
